import {
  ISurveyAnswers,
  ISurveyContract,
  ISurveyUpdateContract,
} from "../zeos-api/contracts/Survey";
import { QuestionTypeEnum } from "../zeos-api/enums/Survey";
import {
  flattenSurveyQuestions,
  getGateQuestion,
  isGateYesSelected,
  isQuestionType,
} from "./SurveyVisibilityHelpers";

export { isGateYesSelected, flattenSurveyQuestions, getGateQuestion };

export const buildSurveyUpdatePayload = (
  template: ISurveyContract,
  zeosUserId: number,
  formData: Record<number, any>,
  options?: { includeOnlyGate?: boolean; completionMode?: string }
): ISurveyUpdateContract => {
  const questions = flattenSurveyQuestions(template);
  const qType = new Map<number, QuestionTypeEnum>();
  questions.forEach((q) => {
    qType.set(q.questionId, q.questionTypeId);
  });

  const gateId = getGateQuestion(template)?.questionId;
  const responseOptions: ISurveyAnswers[] = [];

  Object.entries(formData).forEach(([questionIdStr, value]) => {
    const questionId = parseInt(questionIdStr, 10);
    if (
      options?.includeOnlyGate &&
      gateId != null &&
      questionId !== gateId
    ) {
      return;
    }

    const type = qType.get(questionId);

    if (
      isQuestionType(type ?? QuestionTypeEnum.Unknown, QuestionTypeEnum.FreeText)
    ) {
      const text = typeof value === "string" ? value.trim() : "";
      if (text) {
        responseOptions.push({
          questionId,
          responseOptionId: null,
          freeTextValue: text,
        });
      }
      return;
    }

    if (
      isQuestionType(
        type ?? QuestionTypeEnum.Unknown,
        QuestionTypeEnum.RankedChoice
      ) &&
      Array.isArray(value)
    ) {
      value.forEach((resId, index) => {
        responseOptions.push({
          questionId,
          responseOptionId: Number(resId),
          rankOrder: index + 1,
        });
      });
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((resId) => {
        responseOptions.push({
          questionId,
          responseOptionId: Number(resId),
        });
      });
    } else if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !isNaN(Number(value))
    ) {
      responseOptions.push({
        questionId,
        responseOptionId: Number(value),
      });
    }
  });

  return {
    surveyTemplateId: template.surveyTemplateId,
    zeosUserId,
    ...(options?.completionMode
      ? { completionMode: options.completionMode }
      : {}),
    answers: responseOptions,
  };
};

export const hydrateSurveyAnswers = (
  template: ISurveyContract,
  answers: ISurveyAnswers[]
): Record<number, any> => {
  const qType = new Map<number, QuestionTypeEnum>();
  flattenSurveyQuestions(template).forEach((q) => {
    qType.set(q.questionId, q.questionTypeId);
  });

  const next: Record<number, any> = {};
  const grouped: Record<number, number[]> = {};
  const ranked: Record<
    number,
    Array<{ optionId: number; rankOrder: number }>
  > = {};

  for (const answer of answers) {
    const questionId = answer.questionId;
    const type = qType.get(questionId);

    if (
      isQuestionType(type ?? QuestionTypeEnum.Unknown, QuestionTypeEnum.FreeText)
    ) {
      if (answer.freeTextValue) {
        next[questionId] = answer.freeTextValue;
      }
      continue;
    }

    if (answer.responseOptionId == null) {
      continue;
    }

    if (
      isQuestionType(
        type ?? QuestionTypeEnum.Unknown,
        QuestionTypeEnum.RankedChoice
      )
    ) {
      (ranked[questionId] ??= []).push({
        optionId: Number(answer.responseOptionId),
        rankOrder: answer.rankOrder ?? Number.MAX_SAFE_INTEGER,
      });
      continue;
    }

    (grouped[questionId] ??= []).push(Number(answer.responseOptionId));
  }

  Object.entries(ranked).forEach(([qidStr, vals]) => {
    const qid = Number(qidStr);
    next[qid] = vals
      .slice()
      .sort((a, b) => a.rankOrder - b.rankOrder)
      .map((v) => v.optionId);
  });

  Object.entries(grouped).forEach(([qidStr, vals]) => {
    const qid = Number(qidStr);
    const isMulti = isQuestionType(
      qType.get(qid) ?? QuestionTypeEnum.Unknown,
      QuestionTypeEnum.MultiChoice
    );
    next[qid] = isMulti ? Array.from(new Set(vals)) : vals[0];
  });

  return next;
};
